import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { UIContext } from "../../../providers/UIProvider";
import { useData } from "../../../providers/DataProvider";
import httpClient from "../../../utils/httpClient";
import {
  DateFilterGroup,
  loadFilterOptions as loadFilterOptionsUtil,
} from "../../../components/PayrollManagementBase/PayrollManagementBase";
import { CompanyProps, RegionProps } from "../../../providers/types";

type PayrollCycleStatus = "PayrollSkipped" | "IncludedInPayroll";
type DateFilterType = "none" | "dateRange" | "before" | "after" | "month";

interface DateFilter {
  filterType: "None" | "Exact" | "Between" | "Before" | "After" | "Month";
  start?: string;
  end?: string;
}

interface SearchFilters {
  payrollCycleStatus: PayrollCycleStatus;
  bonusTypeId: 1 | 2;
  companyIds: number[];
  regionIds: number[];
  projectNumbers: string[];
  jobAddress: string;
  bonusTypeDate?: DateFilter;
  opsApprovedDate?: DateFilter;
}

interface SearchRequest {
  pagination: { pageNumber: number; pageSize: number };
  sorting: { sortBy: "OpsApprovedOn"; sortDirection: "Desc" };
  filters: SearchFilters;
}

interface LookupDto {
  id: number;
  name: string;
}

interface PaySkipCycleJob {
  jobId: number;
  bonusType: LookupDto;
  region: LookupDto;
  company: LookupDto;
  communityName: string;
  projectAndJobNumber: string;
  jobAddress: string;
  bonusTypeDate?: string;
  opsApprovedBy: string;
  opsApprovedOn?: string;
}

interface SearchResponse {
  data: PaySkipCycleJob[];
}

interface MultiSelectOption {
  id: number | string;
  label: string;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function buildDateFilter(
  filterType: DateFilterType,
  month: string,
  start: string,
  end: string,
  before: string,
  after: string
): DateFilter | undefined {
  switch (filterType) {
    case "month":
      return { filterType: "Month", start: month };
    case "dateRange":
      return { filterType: "Between", start, end };
    case "before":
      return { filterType: "Before", start: before };
    case "after":
      return { filterType: "After", start: after };
    default:
      return undefined;
  }
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selected: (number | string)[];
  onChange: (selected: (number | string)[]) => void;
  placeholder?: string;
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder = "All",
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())),
    [options, search]
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (id: number | string) => {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  const clearAll = () => onChange([]);

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length === 1
      ? options.find((o) => o.id === selected[0])?.label ?? `${selected.length} selected`
      : `${selected.length} selected`;

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors"
      >
        <span className={selected.length === 0 ? "text-gray-400" : "text-gray-900"}>
          {displayText}
        </span>
        <div className="flex items-center gap-1">
          {selected.length > 0 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              className="text-gray-400 hover:text-gray-600 cursor-pointer px-1"
              title="Clear"
            >
              ×
            </span>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-400">No results</p>
            ) : (
              filtered.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.id)}
                    onChange={() => toggleOption(opt.id)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-gray-700">{opt.label}</span>
                </label>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button onClick={clearAll} className="text-xs text-blue-600 hover:text-blue-700">
                Clear all ({selected.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PayrollSkipCycle() {
  const { role, bonusType } = useData();
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const [, dispatch] = useContext<any>(UIContext);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingRows, setPendingRows] = useState<PaySkipCycleJob[]>([]);
  const [queuedRows, setQueuedRows] = useState<PaySkipCycleJob[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<Set<number>>(new Set());

  const [filterOptions, setFilterOptions] = useState<{ companies: CompanyProps[]; regions: RegionProps[] }>({
    companies: [],
    regions: [],
  });

  const defaultBonusTypeId = useMemo(
    () => (bonusType === "Stage5Bonus" ? 1 : 2) as 1 | 2,
    [bonusType]
  );
  const [bonusTypeId, setBonusTypeId] = useState<1 | 2>(defaultBonusTypeId);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState<number[]>([]);
  const [selectedCommunityProjectNumbers, setSelectedCommunityProjectNumbers] = useState<string[]>([]);
  const [jobAddress, setJobAddress] = useState("");
  const [bonusTypeDateFilterType, setBonusTypeDateFilterType] = useState<DateFilterType>("none");
  const [bonusTypeDateMonth, setBonusTypeDateMonth] = useState("");
  const [bonusTypeDateStart, setBonusTypeDateStart] = useState("");
  const [bonusTypeDateEnd, setBonusTypeDateEnd] = useState("");
  const [bonusTypeDateBefore, setBonusTypeDateBefore] = useState("");
  const [bonusTypeDateAfter, setBonusTypeDateAfter] = useState("");
  const [opsDateFilterType, setOpsDateFilterType] = useState<DateFilterType>("none");
  const [opsDateMonth, setOpsDateMonth] = useState("");
  const [opsDateStart, setOpsDateStart] = useState("");
  const [opsDateEnd, setOpsDateEnd] = useState("");
  const [opsDateBefore, setOpsDateBefore] = useState("");
  const [opsDateAfter, setOpsDateAfter] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const userRoles = ((accounts[0]?.idTokenClaims?.roles as string[] | undefined) ?? []);
  const canAccess = userRoles.includes("OpsPlus") && role === "OpsPlus";

  useEffect(() => {
    if (accounts[0]?.idTokenClaims?.roles && !canAccess) {
      navigate("/", { replace: true });
    }
  }, [accounts, canAccess, navigate]);

  useEffect(() => {
    loadFilterOptionsUtil()
      .then(setFilterOptions)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    setBonusTypeId(defaultBonusTypeId);
  }, [defaultBonusTypeId]);

  const companyOptions = useMemo<MultiSelectOption[]>(
    () => filterOptions.companies.map((c) => ({ id: c.companyId, label: c.companyName })),
    [filterOptions.companies]
  );
  const regionOptions = useMemo<MultiSelectOption[]>(
    () =>
      filterOptions.regions
        .filter((r) => r.regionId > 0)
        .map((r) => ({ id: r.regionId, label: r.regionName })),
    [filterOptions.regions]
  );
  const communityOptions = useMemo<MultiSelectOption[]>(() => {
    const seen = new Set<string>();
    const options: MultiSelectOption[] = [];
    [...pendingRows, ...queuedRows].forEach((row) => {
      const projectNumber = row.projectAndJobNumber?.split("-")[0] ?? "";
      if (!projectNumber || seen.has(projectNumber)) return;
      seen.add(projectNumber);
      options.push({ id: projectNumber, label: row.communityName || projectNumber });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [pendingRows, queuedRows]);

  const baseSearchFilters = useMemo(
    () => ({
      bonusTypeId,
      companyIds: selectedCompanyIds,
      regionIds: selectedRegionIds,
      projectNumbers: selectedCommunityProjectNumbers,
      jobAddress: jobAddress.trim(),
      bonusTypeDate: buildDateFilter(
        bonusTypeDateFilterType,
        bonusTypeDateMonth,
        bonusTypeDateStart,
        bonusTypeDateEnd,
        bonusTypeDateBefore,
        bonusTypeDateAfter
      ),
      opsApprovedDate: buildDateFilter(opsDateFilterType, opsDateMonth, opsDateStart, opsDateEnd, opsDateBefore, opsDateAfter),
    }),
    [
      bonusTypeId,
      selectedCompanyIds,
      selectedRegionIds,
      selectedCommunityProjectNumbers,
      jobAddress,
      bonusTypeDateFilterType,
      bonusTypeDateMonth,
      bonusTypeDateStart,
      bonusTypeDateEnd,
      bonusTypeDateBefore,
      bonusTypeDateAfter,
      opsDateFilterType,
      opsDateMonth,
      opsDateStart,
      opsDateEnd,
      opsDateBefore,
      opsDateAfter,
    ]
  );

  const fetchRows = useCallback(
    async (status: PayrollCycleStatus) => {
      const payload: SearchRequest = {
        pagination: { pageNumber: 1, pageSize: 10000 },
        sorting: { sortBy: "OpsApprovedOn", sortDirection: "Desc" },
        filters: { ...baseSearchFilters, payrollCycleStatus: status },
      };
      const res = await httpClient.post<SearchResponse>("/api/payroll/skip-cycle/search", payload, {
        headers: { roleType: role },
      });
      return res.data?.data ?? [];
    },
    [baseSearchFilters, role]
  );

  const loadData = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const [pending, queued] = await Promise.all([
        fetchRows("PayrollSkipped"),
        fetchRows("IncludedInPayroll"),
      ]);
      setPendingRows(pending);
      setQueuedRows(queued);
      setSelectedJobs(new Set());
    } catch {
      dispatch({
        type: "Snackbar",
        payload: { show: true, message: "Failed to load payroll queue data.", severity: "error" },
      });
    } finally {
      setLoading(false);
    }
  }, [canAccess, dispatch, fetchRows]);

  const handleSearch = useCallback(() => {
    const bonusTypeDateMissing =
      bonusTypeDateFilterType !== "none" &&
      (() => {
        switch (bonusTypeDateFilterType) {
          case "month":
            return !bonusTypeDateMonth;
          case "dateRange":
            return !bonusTypeDateStart || !bonusTypeDateEnd;
          case "before":
            return !bonusTypeDateBefore;
          case "after":
            return !bonusTypeDateAfter;
          default:
            return false;
        }
      })();

    const opsDateMissing =
      opsDateFilterType !== "none" &&
      (() => {
        switch (opsDateFilterType) {
          case "month":
            return !opsDateMonth;
          case "dateRange":
            return !opsDateStart || !opsDateEnd;
          case "before":
            return !opsDateBefore;
          case "after":
            return !opsDateAfter;
          default:
            return false;
        }
      })();

    if (bonusTypeDateMissing || opsDateMissing) {
      const parts = [
        bonusTypeDateMissing &&
          (bonusTypeId === 1 ? "Stage 5 Date" : "Close Date"),
        opsDateMissing && "Ops Approved Date",
      ].filter(Boolean) as string[];
      dispatch({
        type: "Snackbar",
        payload: {
          show: true,
          message: `Please set date values for: ${parts.join(" and ")}.`,
          severity: "warning",
        },
      });
      return;
    }
    void loadData();
  }, [
    bonusTypeDateFilterType,
    bonusTypeDateMonth,
    bonusTypeDateStart,
    bonusTypeDateEnd,
    bonusTypeDateBefore,
    bonusTypeDateAfter,
    opsDateFilterType,
    opsDateMonth,
    opsDateStart,
    opsDateEnd,
    opsDateBefore,
    opsDateAfter,
    bonusTypeId,
    dispatch,
    loadData,
  ]);

  useEffect(() => {
    loadData();
    // Run initial load once; filter edits should not auto-trigger backend calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!canAccess) return null;

  const toggleJob = (jobId: number) => {
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const toggleAllPending = () => {
    if (pendingRows.length > 0 && selectedJobs.size === pendingRows.length) {
      setSelectedJobs(new Set());
      return;
    }
    setSelectedJobs(new Set(pendingRows.map((row) => row.jobId)));
  };

  const moveSelectedToQueue = async () => {
    if (selectedJobs.size === 0) return;
    setSaving(true);
    try {
      const payload = Array.from(selectedJobs).map((jobId) => ({
        jobId,
        isPayrollCycleSkipped: false,
        comment: "Moved to payroll queue.",
      }));
      await httpClient.post(`/api/payroll/skip-cycle/update?bonusTypeId=${bonusTypeId}`, payload, {
        headers: { roleType: role },
      });
      dispatch({
        type: "Snackbar",
        payload: { show: true, message: "Selected records moved to payroll queue.", severity: "success" },
      });
      await loadData();
    } catch {
      dispatch({
        type: "Snackbar",
        payload: { show: true, message: "Failed to move selected records.", severity: "error" },
      });
    } finally {
      setSaving(false);
    }
  };

  const clearFilters = () => {
    setBonusTypeId(defaultBonusTypeId);
    setSelectedCompanyIds([]);
    setSelectedRegionIds([]);
    setSelectedCommunityProjectNumbers([]);
    setJobAddress("");
    setBonusTypeDateFilterType("none");
    setBonusTypeDateMonth("");
    setBonusTypeDateStart("");
    setBonusTypeDateEnd("");
    setBonusTypeDateBefore("");
    setBonusTypeDateAfter("");
    setOpsDateFilterType("none");
    setOpsDateMonth("");
    setOpsDateStart("");
    setOpsDateEnd("");
    setOpsDateBefore("");
    setOpsDateAfter("");
  };

  const bonusTypeDateColumnLabel = bonusTypeId === 1 ? "Stage 5 Date" : "Close Date";

  return (
    <main className="flex-grow p-4">
      <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-semibold text-gray-900">Ops Approved List</h3>
              <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1.5 text-sm font-semibold text-blue-800 border border-blue-200">
                {bonusTypeId === 1 ? "Stage 5 Bonus" : "Closing Bonus"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setFiltersExpanded((prev) => !prev)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
            >
              <span>{filtersExpanded ? "Hide Filters" : "Show Filters"}</span>
              <svg
                className={`w-4 h-4 transition-transform ${filtersExpanded ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">Ops Approved records available to be moved into payroll queue.</p>
          <span className="mt-2 inline-flex rounded-md bg-yellow-200 px-2.5 py-1 text-sm font-bold text-yellow-900">
            Pending records: {pendingRows.length}
          </span>

          {filtersExpanded && (
            <div className="mt-4 p-4 bg-white rounded-md border border-gray-300">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bonus Type</label>
                  <select
                    value={bonusTypeId}
                    onChange={(e) => setBonusTypeId(Number(e.target.value) as 1 | 2)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  >
                    <option value={1}>Stage 5 Bonus</option>
                    <option value={2}>Closing Bonus</option>
                  </select>
                </div>
                <MultiSelectDropdown
                  label="Company"
                  options={companyOptions}
                  selected={selectedCompanyIds}
                  onChange={(ids) => setSelectedCompanyIds(ids as number[])}
                  placeholder="All Companies"
                />
                <MultiSelectDropdown
                  label="Region"
                  options={regionOptions}
                  selected={selectedRegionIds}
                  onChange={(ids) => setSelectedRegionIds(ids as number[])}
                  placeholder="All Regions"
                />
                <MultiSelectDropdown
                  label="Community"
                  options={communityOptions}
                  selected={selectedCommunityProjectNumbers}
                  onChange={(ids) => setSelectedCommunityProjectNumbers(ids as string[])}
                  placeholder="All Communities"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Address</label>
                  <input
                    type="text"
                    value={jobAddress}
                    onChange={(e) => setJobAddress(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <DateFilterGroup
                  label={bonusTypeId === 1 ? "Stage 5 Date Filter" : "Close Date Filter"}
                  filterType={bonusTypeDateFilterType as any}
                  month={bonusTypeDateMonth}
                  startDate={bonusTypeDateStart}
                  endDate={bonusTypeDateEnd}
                  beforeDate={bonusTypeDateBefore}
                  afterDate={bonusTypeDateAfter}
                  onTypeChange={(value) => setBonusTypeDateFilterType(value as DateFilterType)}
                  onMonthChange={setBonusTypeDateMonth}
                  onStartChange={setBonusTypeDateStart}
                  onEndChange={setBonusTypeDateEnd}
                  onBeforeChange={setBonusTypeDateBefore}
                  onAfterChange={setBonusTypeDateAfter}
                />
                <DateFilterGroup
                  label="Ops Approved Date"
                  filterType={opsDateFilterType as any}
                  month={opsDateMonth}
                  startDate={opsDateStart}
                  endDate={opsDateEnd}
                  beforeDate={opsDateBefore}
                  afterDate={opsDateAfter}
                  onTypeChange={(value) => setOpsDateFilterType(value as DateFilterType)}
                  onMonthChange={setOpsDateMonth}
                  onStartChange={setOpsDateStart}
                  onEndChange={setOpsDateEnd}
                  onBeforeChange={setOpsDateBefore}
                  onAfterChange={setOpsDateAfter}
                />
              </div>

              <div className="mt-4 flex items-center gap-3">
                <Button variant="contained" onClick={handleSearch} disabled={loading}>
                  Search
                </Button>
                <Button variant="outlined" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                  <input
                    type="checkbox"
                    checked={pendingRows.length > 0 && selectedJobs.size === pendingRows.length}
                    onChange={toggleAllPending}
                    className="h-4 w-4"
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Project & Job</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Region</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Company</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Community</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Address</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{bonusTypeDateColumnLabel}</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Ops Reviewed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-yellow-300">
              {pendingRows.map((row) => (
                <tr key={`pending-${row.bonusType.id}-${row.jobId}`} className="bg-yellow-50 hover:bg-yellow-100">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedJobs.has(row.jobId)}
                      onChange={() => toggleJob(row.jobId)}
                      className="h-4 w-4"
                    />
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.projectAndJobNumber}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.region.name || "—"}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.company.name || "—"}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.communityName || "—"}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.jobAddress || "—"}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{formatDate(row.bonusTypeDate)}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">
                    {row.opsApprovedBy || row.opsApprovedOn ? (
                      <div className="flex flex-col">
                        {row.opsApprovedBy && <span className="text-xs font-semibold text-gray-500">{row.opsApprovedBy}</span>}
                        <span>{formatDate(row.opsApprovedOn)}</span>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
              {!loading && pendingRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-500">
                    No skipped records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="py-4 flex justify-center">
          <Button
            variant="contained"
            onClick={moveSelectedToQueue}
            disabled={loading || saving || selectedJobs.size === 0}
          >
            Move Selected to Payroll Queue ({selectedJobs.size})
          </Button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-900">Payroll Queue</h3>
          <p className="text-sm text-gray-600 mt-1">Records ready for payroll processing.</p>
          <span className="mt-2 inline-flex rounded-md bg-green-200 px-2.5 py-1 text-sm font-bold text-green-900">
            Queued records: {queuedRows.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Project & Job</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Region</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Company</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Community</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Address</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{bonusTypeDateColumnLabel}</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Ops Reviewed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-green-300">
              {queuedRows.map((row) => (
                <tr key={`queued-${row.bonusType.id}-${row.jobId}`} className="bg-green-50 hover:bg-green-100">
                  <td className="px-3 py-2 text-sm text-gray-700">{row.projectAndJobNumber}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.region.name || "—"}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.company.name || "—"}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.communityName || "—"}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{row.jobAddress || "—"}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{formatDate(row.bonusTypeDate)}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">
                    {row.opsApprovedBy || row.opsApprovedOn ? (
                      <div className="flex flex-col">
                        {row.opsApprovedBy && <span className="text-xs font-semibold text-gray-500">{row.opsApprovedBy}</span>}
                        <span>{formatDate(row.opsApprovedOn)}</span>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
              {!loading && queuedRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500">
                    No queued records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
